import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import './VendorAccessManager.css';
import Footer from '../components/Footer';

function VendorAccessManager() {
  const [vendors, setVendors] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newVendor, setNewVendor] = useState({
    code: '',
    name: '',
    assignedEvents: [],
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // Safe initialization with duplicate prevention
  const initializeVendors = async () => {
    try {
      const vendorsRef = collection(db, 'vendors');
      const vendorsSnapshot = await getDocs(vendorsRef);

      // Check for existing vendors
      if (!vendorsSnapshot.empty) {
        setIsInitialized(true);
        return vendorsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      }

      setIsInitialized(true);
      return [];
    } catch (error) {
      console.error('Initialization error:', error);
      return [];
    }
  };

const fetchAllEvents = async () => {
  try {
    const eventsSnapshot = await getDocs(collection(db, 'events'));
    return eventsSnapshot.docs.map(doc => ({
      id: doc.id,
      name: doc.data().name?.trim(),
      datetime: doc.data().datetime || '',
    }));
  } catch (error) {
    console.error('Error fetching events:', error);
    return [];
  }
};

  // Main data loading effect
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const [vendorsData, eventsData] = await Promise.all([
  initializeVendors(),
  fetchAllEvents(),
]);

        if (!isMounted) return;

        setVendors(vendorsData);
        setEvents(eventsData);
        setLoading(false);
      } catch (error) {
        if (!isMounted) return;
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleAddVendor = async () => {
    if (!newVendor.code || !newVendor.name) {
      alert('Please enter both vendor code and name');
      return;
    }

    try {
      // Check for duplicate vendor code
      const vendorQuery = query(
        collection(db, 'vendors'),
        where('code', '==', newVendor.code)
      );
      const querySnapshot = await getDocs(vendorQuery);

      if (!querySnapshot.empty) {
        alert('Vendor code already exists');
        return;
      }

      // Add new vendor
      const docRef = await addDoc(collection(db, 'vendors'), {
        ...newVendor,
        createdAt: new Date(),
      });

      setVendors([
        ...vendors,
        {
          id: docRef.id,
          ...newVendor,
        },
      ]);

      setNewVendor({
        code: '',
        name: '',
        assignedEvents: [],
      });
    } catch (error) {
      console.error('Error adding vendor:', error);
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (window.confirm('Are you sure you want to delete this vendor?')) {
      try {
        await deleteDoc(doc(db, 'vendors', vendorId));
        setVendors(vendors.filter((vendor) => vendor.id !== vendorId));
      } catch (error) {
        console.error('Error deleting vendor:', error);
      }
    }
  };

  const handleEventAssignment = async (vendorId, eventId, isChecked) => {
    try {
      const vendor = vendors.find((v) => v.id === vendorId);
      let updatedEvents = [...vendor.assignedEvents];

      if (isChecked) {
        updatedEvents.push(eventId);
      } else {
        updatedEvents = updatedEvents.filter((id) => id !== eventId);
      }

      await updateDoc(doc(db, 'vendors', vendorId), {
        assignedEvents: updatedEvents,
      });

      setVendors(
        vendors.map((v) =>
          v.id === vendorId ? { ...v, assignedEvents: updatedEvents } : v
        )
      );
    } catch (error) {
      console.error('Error updating vendor events:', error);
    }
  };

  if (loading) {
    return (
      <div className="vendor-access-container">
        <div className="loading">Loading vendor data...</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="vendor-access-container">
      <div className="vendor-content">
        <h1>Vendor Access Manager</h1>

        <div className="add-vendor-section">
          <h2>Add New Vendor</h2>
          <div className="add-vendor-form">
            <input
              type="text"
              placeholder="Vendor Code (e.g. VEN003)"
              value={newVendor.code}
              onChange={(e) => setNewVendor({ ...newVendor, code: e.target.value })}
            />
            <input
              type="text"
              placeholder="Vendor Name (e.g. Food Vendor)"
              value={newVendor.name}
              onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
            />
            <button className="add-btn" onClick={handleAddVendor}>
              + Add Vendor
            </button>
          </div>
        </div>

        <div className="vendors-list-section">
          <h2>Vendor List <span className="vendor-count">{vendors.length} vendors</span></h2>
          {vendors.length === 0 ? (
            <div className="empty-state">
              <p>No vendors yet. Add one above to get started.</p>
            </div>
          ) : (
            <div className="vendor-cards">
              {vendors.map((vendor) => (
                <div key={vendor.id} className="vendor-card">
                  <div className="vendor-card-header">
                    <div>
                      <span className="vendor-code-badge">{vendor.code}</span>
                      <span className="vendor-name">{vendor.name}</span>
                    </div>
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteVendor(vendor.id)}
                    >
                      Delete
                    </button>
                  </div>

                  <div className="vendor-card-body">
                    <p className="events-label">Assigned Events:</p>
                    {events.length === 0 ? (
                      <p className="no-events">No events available</p>
                    ) : (
                      <div className="event-checkboxes">
                        {events.map((event) => (
                          <label key={event.id} className="event-checkbox">
                            <input
                              type="checkbox"
                              checked={(vendor.assignedEvents || []).includes(event.id)}
                              onChange={(e) =>
                                handleEventAssignment(vendor.id, event.id, e.target.checked)
                              }
                            />
                            <span className="checkbox-label">
                              {event.name}
                              {event.datetime && (
                                <span className="event-date"> — {event.datetime}</span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="vendor-card-footer">
                    <span className="assigned-count">
                      {(vendor.assignedEvents || []).filter(id => events.some(e => e.id === id)).length} event(s) assigned
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default VendorAccessManager;